import { makeAutoObservable, runInAction } from "mobx";
import type { RootStore } from "@intrasync/stores";

/**
 * Fiscal Period Status
 */
export type FiscalPeriodStatus = "open" | "closed" | "locked";

/**
 * Fiscal Period record
 * Per Phase 5: Month-End Close Functionality
 */
export interface FiscalPeriod {
  id: string;
  companyId: string;
  companyName: string;
  year: number;
  month: number;
  startDate: string;
  endDate: string;
  status: FiscalPeriodStatus;
  closedBy?: string;
  closedByName?: string;
  closedAt?: string;
  closeNotes?: string;
  lastReopenedBy?: string;
  lastReopenedByName?: string;
  lastReopenedAt?: string;
  reopenReason?: string;
  unpostedTransactions?: number;
}

/**
 * CPA Adjustment record
 */
export interface CPAAdjustment {
  id: string;
  companyId: string;
  fiscalPeriodId: string;
  adjustmentDate: string; // Always Dec 31 of prior year
  description: string;
  amount: number;
  journalEntryId: string;
  createdBy: string;
  createdByName: string;
  createdAt: string;
}

/**
 * Fiscal period management authorization
 *
 * Authorization is determined by the user's role from the auth system.
 * The "period_manager" or "admin" role grants access to close/reopen/lock periods.
 * Actual authorization is also enforced server-side via the API.
 */
const PERIOD_MANAGER_ROLES = ["period_manager", "admin", "accounting_admin"];

/**
 * Period Password verification - MUST be done server-side
 * SECURITY: Password verification is handled by the API, never stored client-side
 */

/**
 * Pending action type for password verification
 */
export type PendingPeriodAction = {
  type: "close" | "reopen" | "lock";
  periodId: string;
  notes?: string;
  reason?: string;
} | null;

/**
 * FiscalPeriods Store
 *
 * Manages fiscal period data per Phase 5
 * - View period status by company
 * - Close/reopen periods (restricted access)
 * - CPA adjustments (April-September only)
 * - Password verification for period actions
 */
class FiscalPeriodsStore {
  rootStore: RootStore;

  // Selection
  selectedPeriodId: string | null = null;
  selectedCompanyId: string = "AX";

  // UI State
  error: string | null = null;

  // Dialog state
  closeDialogOpen = false;
  reopenDialogOpen = false;
  cpaDialogOpen = false;

  // Password verification state
  passwordDialogOpen = false;
  pendingAction: PendingPeriodAction = null;
  isVerifyingPassword = false;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  // ==================== ServiceStore Access ====================

  get fiscalPeriodsServiceStore() {
    return this.rootStore.serviceStores?.["fiscal-periods"];
  }

  get closedPeriodAdjustmentsServiceStore() {
    return this.rootStore.serviceStores?.["closed-period-adjustments"];
  }

  // ==================== Computed: Data ====================

  get fiscalPeriods(): FiscalPeriod[] {
    return (this.fiscalPeriodsServiceStore?.entities as FiscalPeriod[]) || [];
  }

  get cpaAdjustments(): CPAAdjustment[] {
    return (this.closedPeriodAdjustmentsServiceStore?.entities as CPAAdjustment[]) || [];
  }

  get isLoading(): boolean {
    return this.fiscalPeriodsServiceStore?.isFindPending || false;
  }

  /**
   * Get current user
   */
  get currentUser(): { username: string; role: string } | null {
    const authStore = (this.rootStore as any).authStore;
    const user = authStore?.currentUser;
    return user ? { username: user.username || user.email, role: user.role } : null;
  }

  /**
   * Check if current user can manage fiscal periods
   * Uses role-based access control from the auth system
   */
  get canManagePeriods(): boolean {
    if (!this.currentUser) return false;
    return PERIOD_MANAGER_ROLES.includes(this.currentUser.role);
  }

  /**
   * Check if CPA adjustments are allowed (April-September only)
   */
  get canCreateCPAAdjustment(): boolean {
    if (!this.canManagePeriods) return false;
    const currentMonth = new Date().getMonth() + 1; // 1-12
    return currentMonth >= 4 && currentMonth <= 9;
  }

  /**
   * Selected fiscal period
   */
  get selectedPeriod(): FiscalPeriod | null {
    if (!this.selectedPeriodId) return null;
    return this.fiscalPeriods.find((p) => p.id === this.selectedPeriodId) || null;
  }

  /**
   * Filtered periods by selected company
   */
  get filteredPeriods(): FiscalPeriod[] {
    return this.fiscalPeriods
      .filter((p) => p.companyId === this.selectedCompanyId)
      .sort((a, b) => {
        // Sort by year desc, then month desc
        if (a.year !== b.year) return b.year - a.year;
        return b.month - a.month;
      });
  }

  /**
   * Current period for selected company
   */
  get currentPeriod(): FiscalPeriod | null {
    const now = new Date();
    return (
      this.fiscalPeriods.find(
        (p) =>
          p.companyId === this.selectedCompanyId &&
          p.year === now.getFullYear() &&
          p.month === now.getMonth() + 1
      ) || null
    );
  }

  /**
   * Periods with unposted transactions
   */
  get periodsWithUnposted(): FiscalPeriod[] {
    return this.filteredPeriods.filter(
      (p) => p.unpostedTransactions && p.unpostedTransactions > 0
    );
  }

  /**
   * Load fiscal periods
   */
  async loadFiscalPeriods(): Promise<void> {
    this.error = null;
    try {
      await this.fiscalPeriodsServiceStore?.find({ query: { $limit: 500 } });
    } catch (error) {
      runInAction(() => {
        this.error = error instanceof Error ? error.message : "Failed to load fiscal periods";
      });
    }
  }

  /**
   * Load CPA adjustments for a period
   */
  async loadCPAAdjustments(fiscalPeriodId: string): Promise<void> {
    try {
      await this.closedPeriodAdjustmentsServiceStore?.find({
        query: { fiscalPeriodId, isCpaAdjustment: true, $limit: 100 },
      });
    } catch (error) {
      // Error loading CPA adjustments
    }
  }

  /**
   * Close a fiscal period
   */
  async closePeriod(periodId: string, notes: string): Promise<boolean> {
    if (!this.canManagePeriods) {
      this.error = "You do not have permission to close fiscal periods";
      return false;
    }

    const period = this.fiscalPeriods.find((p) => p.id === periodId);
    if (!period) {
      this.error = "Period not found";
      return false;
    }

    // Check for unposted transactions
    if (period.unpostedTransactions && period.unpostedTransactions > 0) {
      this.error = `Cannot close period with ${period.unpostedTransactions} unposted transaction(s)`;
      return false;
    }

    try {
      await this.fiscalPeriodsServiceStore?.patch(periodId, {
        status: "closed",
        closeNotes: notes,
      });
      return true;
    } catch (error) {
      runInAction(() => {
        this.error = error instanceof Error ? error.message : "Failed to close period";
      });
      return false;
    }
  }

  /**
   * Reopen a fiscal period
   */
  async reopenPeriod(periodId: string, reason: string): Promise<boolean> {
    if (!this.canManagePeriods) {
      this.error = "You do not have permission to reopen fiscal periods";
      return false;
    }

    const period = this.fiscalPeriods.find((p) => p.id === periodId);
    if (!period) {
      this.error = "Period not found";
      return false;
    }

    if (period.status === "locked") {
      this.error = "Cannot reopen a locked period";
      return false;
    }

    try {
      await this.fiscalPeriodsServiceStore?.patch(periodId, {
        status: "open",
        reopenReason: reason,
      });
      return true;
    } catch (error) {
      runInAction(() => {
        this.error = error instanceof Error ? error.message : "Failed to reopen period";
      });
      return false;
    }
  }

  /**
   * Lock a fiscal period (permanent)
   */
  async lockPeriod(periodId: string): Promise<boolean> {
    if (!this.canManagePeriods) {
      this.error = "You do not have permission to lock fiscal periods";
      return false;
    }

    const period = this.fiscalPeriods.find((p) => p.id === periodId);
    if (!period) {
      this.error = "Period not found";
      return false;
    }

    if (period.status !== "closed") {
      this.error = "Can only lock closed periods";
      return false;
    }

    try {
      await this.fiscalPeriodsServiceStore?.patch(periodId, {
        status: "locked",
      });
      return true;
    } catch (error) {
      runInAction(() => {
        this.error = error instanceof Error ? error.message : "Failed to lock period";
      });
      return false;
    }
  }

  /**
   * Create CPA adjustment
   */
  async createCPAAdjustment(
    description: string,
    amount: number,
    debitAccountId: string,
    creditAccountId: string
  ): Promise<boolean> {
    if (!this.canCreateCPAAdjustment) {
      this.error = "CPA adjustments can only be created between April and September";
      return false;
    }

    try {
      const priorYear = new Date().getFullYear() - 1;
      await this.closedPeriodAdjustmentsServiceStore?.create({
        companyId: this.selectedCompanyId,
        fiscalPeriodId: `${this.selectedCompanyId}-${priorYear}-12`,
        adjustmentDate: `${priorYear}-12-31`,
        description,
        amount,
        debitAccountId,
        creditAccountId,
        isCpaAdjustment: true,
      });
      return true;
    } catch (error) {
      runInAction(() => {
        this.error = error instanceof Error ? error.message : "Failed to create CPA adjustment";
      });
      return false;
    }
  }

  /**
   * Select period
   */
  selectPeriod(periodId: string | null): void {
    this.selectedPeriodId = periodId;
  }

  /**
   * Set selected company
   */
  setSelectedCompany(companyId: string): void {
    this.selectedCompanyId = companyId;
  }

  /**
   * Clear error
   */
  clearError(): void {
    this.error = null;
  }

  // ==================== Password Verification ====================

  /**
   * Request password verification before performing a period action
   */
  requestPasswordVerification(action: PendingPeriodAction): void {
    if (!this.canManagePeriods) {
      this.error = "You do not have permission to manage fiscal periods";
      return;
    }
    this.pendingAction = action;
    this.passwordDialogOpen = true;
  }

  /**
   * Verify the period management password via server-side API
   * SECURITY: Password verification MUST be done server-side
   */
  async verifyPassword(password: string): Promise<boolean> {
    this.isVerifyingPassword = true;
    this.error = null;

    try {
      const verificationService = (this.rootStore as any).app?.service?.('fiscal-periods/verify-password');

      if (!verificationService) {
        runInAction(() => {
          this.error = "Period management password verification service is not available. Contact your administrator.";
          this.isVerifyingPassword = false;
        });
        return false;
      }

      const result = await verificationService.create({ password });
      if (!result?.verified) {
        runInAction(() => {
          this.error = "Incorrect password. Please try again.";
          this.isVerifyingPassword = false;
        });
        return false;
      }

      // Password verified, execute the pending action
      const success = await this.executePendingAction();

      runInAction(() => {
        this.isVerifyingPassword = false;
        if (success) {
          this.closePasswordDialog();
        }
      });

      return success;
    } catch (error) {
      runInAction(() => {
        this.error = error instanceof Error ? error.message : "Password verification failed";
        this.isVerifyingPassword = false;
      });
      return false;
    }
  }

  /**
   * Execute the pending action after password verification
   */
  private async executePendingAction(): Promise<boolean> {
    if (!this.pendingAction) return false;

    const { type, periodId, notes, reason } = this.pendingAction;

    switch (type) {
      case "close":
        return await this.executeClosePeriod(periodId, notes || "");
      case "reopen":
        return await this.executeReopenPeriod(periodId, reason || "");
      case "lock":
        return await this.executeLockPeriod(periodId);
      default:
        return false;
    }
  }

  /**
   * Close the password dialog
   */
  closePasswordDialog(): void {
    this.passwordDialogOpen = false;
    this.pendingAction = null;
    this.isVerifyingPassword = false;
  }

  /**
   * Internal method to execute period close (after password verification)
   */
  private async executeClosePeriod(periodId: string, notes: string): Promise<boolean> {
    const period = this.fiscalPeriods.find((p) => p.id === periodId);
    if (!period) {
      this.error = "Period not found";
      return false;
    }

    if (period.unpostedTransactions && period.unpostedTransactions > 0) {
      this.error = `Cannot close period with ${period.unpostedTransactions} unposted transaction(s)`;
      return false;
    }

    try {
      await this.fiscalPeriodsServiceStore?.patch(periodId, {
        status: "closed",
        closeNotes: notes,
      });
      runInAction(() => {
        this.closeDialogOpen = false;
      });
      return true;
    } catch (error) {
      runInAction(() => {
        this.error = error instanceof Error ? error.message : "Failed to close period";
      });
      return false;
    }
  }

  /**
   * Internal method to execute period reopen (after password verification)
   */
  private async executeReopenPeriod(periodId: string, reason: string): Promise<boolean> {
    const period = this.fiscalPeriods.find((p) => p.id === periodId);
    if (!period) {
      this.error = "Period not found";
      return false;
    }

    if (period.status === "locked") {
      this.error = "Cannot reopen a locked period";
      return false;
    }

    try {
      await this.fiscalPeriodsServiceStore?.patch(periodId, {
        status: "open",
        reopenReason: reason,
      });
      runInAction(() => {
        this.reopenDialogOpen = false;
      });
      return true;
    } catch (error) {
      runInAction(() => {
        this.error = error instanceof Error ? error.message : "Failed to reopen period";
      });
      return false;
    }
  }

  /**
   * Internal method to execute period lock (after password verification)
   */
  private async executeLockPeriod(periodId: string): Promise<boolean> {
    const period = this.fiscalPeriods.find((p) => p.id === periodId);
    if (!period) {
      this.error = "Period not found";
      return false;
    }

    if (period.status !== "closed") {
      this.error = "Can only lock closed periods";
      return false;
    }

    try {
      await this.fiscalPeriodsServiceStore?.patch(periodId, {
        status: "locked",
      });
      return true;
    } catch (error) {
      runInAction(() => {
        this.error = error instanceof Error ? error.message : "Failed to lock period";
      });
      return false;
    }
  }

  /**
   * Get month name
   */
  getMonthName(month: number): string {
    const months = [
      "January", "February", "March", "April", "May", "June",
      "July", "August", "September", "October", "November", "December",
    ];
    return months[month - 1] || "";
  }
}

export default FiscalPeriodsStore;
